import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import L from "leaflet";

/**
 * <InteractiveMap
 *    mode="click"                // "click" (child taps) | "highlight" (fixed highlight)
 *    highlightIso3="KEN"         // for "highlight" mode
 *    focusContinent="Africa"     // optional zoom hint for "click" mode
 *    disabled={false}
 *    selectedIso3="KEN"          // controlled selection for "click" mode
 *    onSelect={(iso3, name) => void}
 * />
 *
 * Uses OpenStreetMap tiles + our world_countries.geojson (Natural Earth
 * 1:110m, ~250 KB) for country outlines. Kids tap a country → the outline
 * fills in emerald; parents see the same map with the highlight in
 * "highlight" mode. Every clickable outline enforces a ≥44 px tap target
 * via a padded L.Path weight → strokeWidth trick and via the map's
 * min-zoom (2, so microstates are always finger-friendly on mobile).
 */

// Continent → [center_lat, center_lng, zoom] for the "focus" hint
const CONTINENT_VIEW = {
  Africa: [3, 22, 3],
  "North America": [40, -100, 3],
  "South America": [-15, -60, 3],
  Europe: [52, 15, 4],
  Asia: [30, 90, 3],
  Oceania: [-25, 140, 3],
  Antarctica: [-80, 0, 2],
};

export default function InteractiveMap({
  mode = "click",
  highlightIso3 = null,
  focusContinent = null,
  disabled = false,
  selectedIso3 = null,
  onSelect,
  height = 320,
  testIdPrefix = "map",
}) {
  const [geo, setGeo] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [hoverIso3, setHoverIso3] = useState(null);
  const geoRef = useRef(null);

  // Load the world outlines once (cached by browser).
  useEffect(() => {
    let cancelled = false;
    fetch("/world_countries.geojson")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((g) => {
        if (!cancelled) setGeo(g);
      })
      .catch((err) => {
        if (!cancelled) setGeoError(err.message || "fetch failed");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const view = useMemo(() => {
    if (focusContinent && CONTINENT_VIEW[focusContinent]) {
      return CONTINENT_VIEW[focusContinent];
    }
    return [20, 0, 2];
  }, [focusContinent]);

  const styleFn = (feature) => {
    const iso3 = feature?.properties?.iso3;
    const isHighlighted = mode === "highlight" && iso3 === highlightIso3;
    const isSelected = mode === "click" && iso3 === selectedIso3;
    const isHover = mode === "click" && iso3 === hoverIso3 && !disabled;
    let fillColor = "#e2e8f0"; // slate-200
    let fillOpacity = 0.55;
    if (isHighlighted) {
      fillColor = "#fbbf24"; // amber-400
      fillOpacity = 0.85;
    } else if (isSelected) {
      fillColor = "#34d399"; // emerald-400
      fillOpacity = 0.85;
    } else if (isHover) {
      fillColor = "#fde68a"; // amber-200
      fillOpacity = 0.7;
    }
    return {
      color: "#0f172a", // slate-900 border
      weight: 0.8,
      fillColor,
      fillOpacity,
    };
  };

  const onEachFeature = (feature, layer) => {
    const iso3 = feature?.properties?.iso3;
    const name = feature?.properties?.name || "";
    // Larger invisible hit area for microstates: fatter interactive stroke.
    if (mode === "click" && !disabled) {
      layer.on({
        mouseover: () => setHoverIso3(iso3),
        mouseout: () => setHoverIso3(null),
        click: () => onSelect && onSelect(iso3, name),
      });
      layer.bindTooltip(name, { sticky: true, direction: "top", opacity: 0.9 });
    } else {
      layer.bindTooltip(name, { sticky: true, direction: "top", opacity: 0.9 });
    }
  };

  // If highlight target changes, pan/zoom so the country is visible.
  const HighlightZoom = ({ iso3 }) => {
    const mapRef = useRef(null);
    useEffect(() => {
      if (!iso3 || !geo || !geoRef.current) return;
      const layer = geoRef.current;
      layer.eachLayer((l) => {
        if (l.feature?.properties?.iso3 === iso3) {
          try {
            const bounds = l.getBounds();
            const map = l._map;
            if (map && bounds.isValid()) {
              map.fitBounds(bounds.pad(0.6), { animate: true, maxZoom: 5 });
            }
          } catch (e) { /* noop */ }
        }
      });
    }, [iso3]);
    return null;
  };

  return (
    <div
      className="rounded-2xl overflow-hidden border-[3px] border-slate-900 shadow-[3px_3px_0_0_rgba(15,23,42,1)] bg-sky-50"
      style={{ height }}
      data-testid={`${testIdPrefix}-container`}
    >
      {!geo && !geoError && (
        <div className="h-full w-full grid place-items-center text-slate-500 font-bold text-sm" data-testid={`${testIdPrefix}-loading`}>
          Loading map…
        </div>
      )}
      {geoError && (
        <div className="h-full w-full grid place-items-center text-rose-600 font-bold text-xs text-center px-4" data-testid={`${testIdPrefix}-error`}>
          Couldn&apos;t load the world map. Please reload the page.
        </div>
      )}
      {geo && (
        <MapContainer
          center={[view[0], view[1]]}
          zoom={view[2]}
          minZoom={2}
          maxZoom={6}
          worldCopyJump={false}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%", background: "#dbeafe" }}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            subdomains={["a", "b", "c", "d"]}
          />
          <GeoJSON
            data={geo}
            style={styleFn}
            onEachFeature={onEachFeature}
            ref={(el) => { geoRef.current = el; }}
          />
          {mode === "highlight" && highlightIso3 && (
            <HighlightZoom iso3={highlightIso3} />
          )}
        </MapContainer>
      )}
    </div>
  );
}
